import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SUBJECTS = [
  'General Question',
  'Billing / Payments',
  'Account Issue',
  'Bug Report',
  'Feature Request',
  'Technical Support',
  'Technician Account Question',
  'Other',
];

const styles = {
  // Floating button — base
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 1200,
    background: '#D4AF37',
    color: '#0a0a0a',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.03em',
    boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
    transition: 'background 0.12s, box-shadow 0.12s, transform 0.08s',
    userSelect: 'none',
  },
  fabHover: {
    background: '#e0bc45',
    boxShadow: '0 4px 18px rgba(0,0,0,0.5)',
  },
  fabActive: {
    background: '#c9a730',
    boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
    transform: 'scale(0.97)',
  },
  // Backdrop
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Modal box
  modal: {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    padding: '28px 28px 24px',
    width: '100%',
    maxWidth: '460px',
    margin: '16px',
    boxSizing: 'border-box',
  },
  modalTitle: {
    margin: '0 0 20px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#f0f0f0',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '6px',
  },
  field: {
    marginBottom: '16px',
  },
  select: {
    width: '100%',
    background: '#111',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#f0f0f0',
    padding: '9px 12px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    background: '#111',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#f0f0f0',
    padding: '9px 12px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '120px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  btnSend: {
    background: '#D4AF37',
    color: '#0a0a0a',
    border: 'none',
    borderRadius: '6px',
    padding: '9px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSendDisabled: {
    background: '#8a7422',
    color: '#555',
    border: 'none',
    borderRadius: '6px',
    padding: '9px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'not-allowed',
  },
  btnCancel: {
    background: 'transparent',
    color: '#888',
    border: '1px solid #333',
    borderRadius: '6px',
    padding: '9px 20px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  error: {
    color: '#ff6b6b',
    fontSize: '13px',
    marginTop: '10px',
  },
  success: {
    color: '#6bcb77',
    fontSize: '14px',
    textAlign: 'center',
    padding: '12px 0',
  },
};

export default function SupportModal() {
  const location = useLocation();
  const [open, setOpen]       = useState(false);
  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [sent, setSent]       = useState(false);
  const [fabHover, setFabHover]   = useState(false);
  const [fabActive, setFabActive] = useState(false);

  const handleOpen = useCallback(() => {
    setSubject(SUBJECTS[0]);
    setMessage('');
    setError('');
    setSent(false);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    setOpen(false);
  }, [loading]);

  // Allow other pages (e.g. the Manage Technician tab) to open THIS existing
  // modal with a category preselected, via a window event. No second modal,
  // no localStorage. If no valid subject is supplied, defaults to the first.
  useEffect(() => {
    const onOpen = (e) => {
      const sub = e && e.detail ? e.detail.subject : null;
      setSubject(sub && SUBJECTS.includes(sub) ? sub : SUBJECTS[0]);
      setMessage('');
      setError('');
      setSent(false);
      setOpen(true);
    };
    window.addEventListener('vi:open-support', onOpen);
    return () => window.removeEventListener('vi:open-support', onOpen);
  }, []);

  const getFabStyle = () => {
    if (fabActive) return { ...styles.fab, ...styles.fabActive };
    if (fabHover)  return { ...styles.fab, ...styles.fabHover };
    return styles.fab;
  };

  const handleSend = useCallback(async () => {
    if (loading) return; // prevent double submission
    if (!message.trim()) {
      setError('Please enter a message.');
      return;
    }
    setError('');
    setLoading(true);

    const params = new URLSearchParams(location.search);
    const payload = {
      subject,
      message: message.trim(),
      page: location.pathname,
      build_id: params.get('build_id') || undefined,
      image_id: params.get('image_id') || undefined,
    };

    try {
      const res = await fetch('/v1/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSent(true);
      setTimeout(() => setOpen(false), 2000);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [subject, message, location, loading]);

  return (
    <>
      <button
        style={{...getFabStyle(), ...((location.pathname.startsWith('/dashboard') && location.pathname !== '/dashboard/visual' && location.pathname !== '/dashboard/professional' && location.pathname !== '/dashboard/battlemaps') ? {display:'none'} : {})}}
        onClick={handleOpen}
        onMouseEnter={() => setFabHover(true)}
        onMouseLeave={() => { setFabHover(false); setFabActive(false); }}
        onMouseDown={() => setFabActive(true)}
        onMouseUp={() => setFabActive(false)}
        aria-label="Open support"
      >
        Support
      </button>

      {open && (
        <div style={styles.backdrop} onClick={handleClose}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Contact Support</h2>

            {sent ? (
              <p style={styles.success}>✓ Message sent. We'll be in touch soon.</p>
            ) : (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>Subject</label>
                  <select
                    style={styles.select}
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    disabled={loading}
                  >
                    {SUBJECTS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Message</label>
                  <textarea
                    style={styles.textarea}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Describe your issue or question..."
                    disabled={loading}
                    maxLength={2000}
                  />
                </div>

                {error && <p style={styles.error}>{error}</p>}

                <div style={styles.actions}>
                  <button
                    style={styles.btnCancel}
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    style={loading ? styles.btnSendDisabled : styles.btnSend}
                    onClick={handleSend}
                    disabled={loading}
                  >
                    {loading ? 'Sending…' : 'Send Message'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
